import { cn } from '@/lib/utils';

export type EdgeFadeSide = 'left' | 'right';

export type EdgeFadeTone = 'light' | 'dark';

export type EdgeFadeProps = {
  /** Which edges show the fade. Defaults to both. */
  sides?: EdgeFadeSide[];
  /** `dark` fades into gray-dark-950 (PageShell). Defaults to `light`. */
  tone?: EdgeFadeTone;
  className?: string;
  children: React.ReactNode;
};

export const EDGE_FADE_LEFT_CLASSES = cn(
  "before:pointer-events-none before:absolute before:inset-y-0 before:left-0 before:z-[1] before:w-16 before:content-['']",
  'before:bg-[linear-gradient(to_right,rgb(var(--color-gray-25)),transparent)]',
  'dark:before:bg-[linear-gradient(to_right,rgb(var(--color-gray-dark-950)),transparent)]'
);

export const EDGE_FADE_RIGHT_CLASSES = cn(
  "after:pointer-events-none after:absolute after:inset-y-0 after:right-0 after:z-[1] after:w-16 after:content-['']",
  'after:bg-[linear-gradient(to_left,rgb(var(--color-gray-25)),transparent)]',
  'dark:after:bg-[linear-gradient(to_left,rgb(var(--color-gray-dark-950)),transparent)]'
);

export const EDGE_FADE_LEFT_DARK_CLASSES = cn(
  "before:pointer-events-none before:absolute before:inset-y-0 before:left-0 before:z-[1] before:w-16 before:content-['']",
  'before:bg-[linear-gradient(to_right,rgb(var(--color-gray-dark-950)),transparent)]'
);

export const EDGE_FADE_RIGHT_DARK_CLASSES = cn(
  "after:pointer-events-none after:absolute after:inset-y-0 after:right-0 after:z-[1] after:w-16 after:content-['']",
  'after:bg-[linear-gradient(to_left,rgb(var(--color-gray-dark-950)),transparent)]'
);

/**
 * Horizontal edge fades for overflow scroll regions.
 * Extracted from PagedCarousel so mobile carousels can share the same treatment.
 */
export function EdgeFade({
  sides = ['left', 'right'],
  tone = 'light',
  className,
  children,
}: EdgeFadeProps) {
  const showLeft = sides.includes('left');
  const showRight = sides.includes('right');
  const leftClasses =
    tone === 'dark' ? EDGE_FADE_LEFT_DARK_CLASSES : EDGE_FADE_LEFT_CLASSES;
  const rightClasses =
    tone === 'dark' ? EDGE_FADE_RIGHT_DARK_CLASSES : EDGE_FADE_RIGHT_CLASSES;

  return (
    <div
      className={cn(
        'relative w-full',
        showLeft && leftClasses,
        showRight && rightClasses,
        className
      )}
    >
      {children}
    </div>
  );
}
